// packages/web/src/components/RecordActions.tsx — R2: generic record actions,
// driven by the type's schema. Renders (a) the LIFECYCLE transitions legal
// from the record's CURRENT state as calm buttons (POST /records/:id/
// transition; the server stays source of truth — an illegal transition 422s
// lifecycle_guard, surfaced calmly here), OR — for a type with a plain
// status-only picklist and no lifecycle — an inline status <select> (PATCH
// /records/:id); and (b) Delete with a confirm step (POST /records/:id/delete,
// soft). R3 reuses this on every detail screen.
import { useState } from 'preact/hooks';
import {
  ApiError,
  transitionRecord,
  updateRecord,
  deleteRecord,
  type RecordRow,
  type RecordTypeSchema,
} from '../lib/api';
import './RecordActions.css';

interface RecordActionsProps {
  tenantId: string;
  schema: RecordTypeSchema;
  record: RecordRow;
  onChanged: (record: RecordRow) => void;
  onDeleted: (record: RecordRow) => void;
}

function humanizeValue(v: string): string {
  return v
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function RecordActions({ tenantId, schema, record, onChanged, onDeleted }: RecordActionsProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // A "status" picklist field with no lifecycle is the inline-status case
  // (e.g. actor_payment.status: owed/received/overdue).
  const statusField = schema.lifecycle
    ? undefined
    : schema.fields.find((f) => f.name === 'status' && f.type === 'picklist');

  async function doTransition(to: string) {
    setBusy(true);
    setError(null);
    try {
      const updated = await transitionRecord(tenantId, record.id, to);
      onChanged(updated);
    } catch (err) {
      // A 422 lifecycle_guard (or any error) is surfaced calmly, never thrown.
      setError(err instanceof ApiError ? err.message : 'Could not change the state.');
    } finally {
      setBusy(false);
    }
  }

  async function doStatus(value: string) {
    setBusy(true);
    setError(null);
    try {
      const updated = await updateRecord(tenantId, record.id, { status: value });
      onChanged(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update status.');
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    setError(null);
    try {
      const deleted = await deleteRecord(tenantId, record.id);
      onDeleted(deleted);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete.');
    } finally {
      setBusy(false);
    }
  }

  const nextStates =
    schema.lifecycle && record.lifecycle_state
      ? schema.lifecycle.transitions[record.lifecycle_state] ?? []
      : [];

  return (
    <div class="ra">
      {schema.lifecycle && (
        <div class="ra__section">
          <div class="ra__label">
            State · <span class="ra__now" data-testid="ra-state">{humanizeValue(record.lifecycle_state ?? '—')}</span>
          </div>
          {nextStates.length === 0 ? (
            <div class="ra__hint">No further transitions.</div>
          ) : (
            <div class="ra__buttons">
              {nextStates.map((to) => (
                <button
                  type="button"
                  key={to}
                  class="ra__btn"
                  disabled={busy}
                  onClick={() => doTransition(to)}
                  data-testid={`ra-to-${to}`}
                >
                  {humanizeValue(to)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {statusField && (
        <div class="ra__section">
          <label class="ra__label" for="ra-status">
            Status
          </label>
          <select
            id="ra-status"
            class="ra__select"
            disabled={busy}
            value={(record.fields['status'] as string) ?? ''}
            onChange={(e) => doStatus((e.target as HTMLSelectElement).value)}
            data-testid="ra-status"
          >
            {(statusField.picklistValues ?? []).map((opt) => (
              <option value={opt} key={opt}>
                {humanizeValue(opt)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div class="ra__section ra__section--danger">
        {confirmDelete ? (
          <div class="ra__confirm">
            <span class="ra__hint">Delete this {schema.label.toLowerCase()}?</span>
            <button type="button" class="ra__btn ra__btn--danger" disabled={busy} onClick={doDelete} data-testid="ra-delete-confirm">
              {busy ? 'Deleting…' : 'Delete'}
            </button>
            <button type="button" class="ra__btn ra__btn--ghost" disabled={busy} onClick={() => setConfirmDelete(false)}>
              Keep
            </button>
          </div>
        ) : (
          <button type="button" class="ra__btn ra__btn--ghost" disabled={busy} onClick={() => setConfirmDelete(true)} data-testid="ra-delete">
            Delete
          </button>
        )}
      </div>

      {error && (
        <div class="ra__error" role="alert" data-testid="ra-error">
          {error}
        </div>
      )}
    </div>
  );
}
